import { useState, type FormEvent } from 'react';
import { Plus, X, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input, Textarea, Select, FieldWrapper, Field } from '@/components/ui/Field';
import { Switch } from '@/components/ui/Switch';
import { MarkdownField } from '@/components/forms/MarkdownField';
import { slugify } from '@/lib/utils';
import type { ServiceRow } from '@/lib/admin-queries';
import { useAdminTopics } from '@/lib/admin-queries';
import { EMPTY_PRICING, PricingEditor, type PricingValue } from './PricingEditor';

const STATUSES = ['draft', 'published', 'archived'] as const;

interface Props {
  value: ServiceRow;
  submitting: boolean;
  onSubmit: (v: ServiceRow) => void | Promise<void>;
  onCancel: () => void;
}

interface ChipListProps {
  label: string;
  items: string[];
  onChange: (items: string[]) => void;
  placeholder?: string;
}

function ChipList({ label, items, onChange, placeholder }: ChipListProps) {
  const [input, setInput] = useState('');
  function add() {
    const v = input.trim();
    if (!v) return;
    if (!items.includes(v)) onChange([...items, v]);
    setInput('');
  }
  return (
    <FieldWrapper label={label}>
      <div className="flex flex-wrap gap-1.5">
        {items.map((it, idx) => (
          <span
            key={`${it}-${idx}`}
            className="inline-flex items-center gap-1 rounded-md border border-border bg-bg-elevated px-2 py-1 text-xs text-fg"
          >
            {it}
            <button
              type="button"
              aria-label={`Remove ${it}`}
              onClick={() => onChange(items.filter((_, i) => i !== idx))}
              className="text-fg-subtle hover:text-danger"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        {items.length === 0 && (
          <span className="text-xs text-fg-subtle">None yet.</span>
        )}
      </div>
      <div className="mt-2 flex gap-2">
        <Input
          placeholder={placeholder ?? 'Add an item'}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
        />
        <Button type="button" variant="secondary" leftIcon={<Plus className="h-3.5 w-3.5" />} onClick={add}>
          Add
        </Button>
      </div>
    </FieldWrapper>
  );
}

function TopicSelect({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  const { data: topics = [] } = useAdminTopics();
  const activeTopics = topics.filter((t) => t.is_active);
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="flex h-11 w-full rounded-md border border-border bg-bg-elevated px-3 text-sm text-fg focus:border-gold-500/60 focus:outline-none focus:ring-2 focus:ring-gold-500/20"
    >
      <option value="">— None —</option>
      {activeTopics.map((t) => (
        <option key={t.id} value={t.id}>
          {t.name}
        </option>
      ))}
    </select>
  );
}

export function ServiceEditForm({ value, submitting, onSubmit, onCancel }: Props) {
  const [v, setV] = useState<ServiceRow>(value);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Pricing lives in jsonb on the DB. Admins edit it through the structured
  // PricingEditor; the JSON-raw view stays behind an "Advanced" disclosure
  // for power users / migrations.
  const initialPricing: PricingValue = value.pricing == null
    ? EMPTY_PRICING
    : normalisePricing(value.pricing);
  const [pricing, setPricing] = useState<PricingValue>(initialPricing);
  const { data: topics = [] } = useAdminTopics();

  function patch<K extends keyof ServiceRow>(key: K, val: ServiceRow[K]) {
    setV((cur) => ({ ...cur, [key]: val }));
  }

  function onTitleBlur() {
    if (!v.slug) patch('slug', slugify(v.title));
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!v.title.trim()) next.title = 'Title is required';
    if (!v.slug.trim()) next.slug = 'Slug is required';
    else if (!/^[a-z0-9-]+$/.test(v.slug)) next.slug = 'Slug must be lowercase letters, numbers and dashes';
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    // An empty pricing means the admin opted out — clear the column. An
    // empty-but-valid editor (no tiers, no monthly, no addons) still serialises
    // to a structured object, so we treat that as "pricing exists" and let it
    // through (the public card hides empty sections).
    const pricingToPersist = isPricingEmpty(pricing) ? null : pricing;
    const cleaned: ServiceRow = {
      ...v,
      slug: v.slug.trim(),
      title: v.title.trim(),
      summary: v.summary?.trim() || null,
      description_md: v.description_md?.trim() || null,
      icon: v.icon?.trim() || null,
      cta_label: v.cta_label?.trim() || 'Starting from',
      cta_href: v.cta_href?.trim() || '/contact',
      meta_title: v.meta_title?.trim() || null,
      meta_description: v.meta_description?.trim() || null,
      topic_id: v.topic_id ?? null,
      pricing: pricingToPersist,
    };
    onSubmit(cleaned);
  }

  return (
    <form id="service-form" onSubmit={handleSubmit} className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <Field id="title" label="Title" required error={errors.title}>
          <Input
            id="title"
            required
            value={v.title}
            onChange={(e) => patch('title', e.target.value)}
            onBlur={onTitleBlur}
            placeholder="Custom Software Development"
          />
        </Field>
        <Field id="slug" label="Slug" required error={errors.slug}>
          <Input
            id="slug"
            required
            value={v.slug}
            onChange={(e) => patch('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
            placeholder="custom-software"
          />
        </Field>
      </div>

      <Field id="summary" label="Summary" hint="One-sentence elevator pitch. Shown under the title.">
        <Textarea
          id="summary"
          rows={2}
          value={v.summary ?? ''}
          onChange={(e) => patch('summary', e.target.value)}
          placeholder="Full-stack bespoke products, from sketch to production."
        />
      </Field>

      <div className="grid gap-4 md:grid-cols-2">
        <Field
          id="icon"
          label="Icon"
          hint="Lucide React icon name (e.g. Code2, Globe, Brain). Leave blank for default."
        >
          <Input
            id="icon"
            value={v.icon ?? ''}
            onChange={(e) => patch('icon', e.target.value)}
            placeholder="Code2"
          />
        </Field>
        <Field id="status_publish" label="Publish" required>
          <Select
            id="status_publish"
            value={v.status_publish}
            onChange={(e) => patch('status_publish', e.target.value as ServiceRow['status_publish'])}
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </Select>
        </Field>
      </div>

      <FieldWrapper label="Description" hint="Markdown. Shown as 'How we work' on the public site.">
        <MarkdownField
          value={v.description_md ?? ''}
          onChange={(s) => patch('description_md', s)}
          rows={8}
        />
      </FieldWrapper>

      <ChipList
        label="Deliverables"
        items={v.deliverables}
        onChange={(items) => patch('deliverables', items)}
        placeholder="What the client gets"
      />
      <ChipList
        label="Tech strip"
        items={v.tech_strip}
        onChange={(items) => patch('tech_strip', items)}
        placeholder="e.g. TypeScript"
      />

      <div className="grid gap-4 md:grid-cols-2">
        <Field
          id="topic_id"
          label="Contact topic"
          hint={
            v.topic_id
              ? 'Which topic is pre-selected when they land on /contact.'
              : 'Which topic is pre-selected when they land on /contact.'
          }
        >
          <div className="space-y-1.5">
            <TopicSelect
              value={v.topic_id ?? ''}
              onChange={(id) => {
                patch('topic_id', id || null);
                const topic = topics.find((t) => t.id === id);
                if (topic) {
                  patch('cta_href', `/contact?topic=${encodeURIComponent(topic.slug)}`);
                }
              }}
            />
            {v.topic_id && (
              <a
                href="/admin/topics"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-gold-500 hover:underline"
              >
                <ExternalLink className="h-3 w-3" />
                Edit “{topics.find((t) => t.id === v.topic_id)?.name ?? 'topic'}” in Topics
              </a>
            )}
          </div>
        </Field>
        <Field id="cta_label" label="CTA label" hint="Text shown on the call-to-action button.">
          <Input
            id="cta_label"
            value={v.cta_label ?? ''}
            onChange={(e) => patch('cta_label', e.target.value)}
            placeholder="Start a project"
          />
        </Field>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Field id="display_order" label="Display order" hint="Lower numbers appear first.">
          <Input
            id="display_order"
            type="number"
            value={v.display_order}
            onChange={(e) => patch('display_order', Number(e.target.value))}
          />
        </Field>
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-fg">Featured</span>
          <span className="mt-2 flex items-center gap-2">
            <Switch
              checked={v.is_featured}
              onCheckedChange={(c) => patch('is_featured', !!c)}
              id="is_featured"
            />
            <span className="text-xs text-fg-muted">Show on home & lists</span>
          </span>
        </label>
      </div>

      <details className="rounded-md border border-border bg-bg-elevated/30 p-4">
        <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-fg-muted">
          SEO & metadata (optional)
        </summary>
        <div className="mt-4 space-y-4">
          <Field id="meta_title" label="Meta title" hint="Overrides the default page title for SEO.">
            <Input
              id="meta_title"
              value={v.meta_title ?? ''}
              onChange={(e) => patch('meta_title', e.target.value)}
              placeholder="Custom Software — Vonlof"
            />
          </Field>
          <Field id="meta_description" label="Meta description" hint="Shown in search engine snippets.">
            <Textarea
              id="meta_description"
              rows={2}
              value={v.meta_description ?? ''}
              onChange={(e) => patch('meta_description', e.target.value)}
            />
          </Field>
        </div>
      </details>

      <details className="rounded-md border border-border bg-bg-elevated/30 p-4" open>
        <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-fg-muted">
          Pricing (optional)
        </summary>
        <div className="mt-4 space-y-4">
          <p className="text-xs text-fg-muted">
            When set, the public /services card replaces the standard deliverables layout with tier cards,
            monthly packages, add-ons, terms, and a bundle callout. Use the structured editor below —
            every field on the public card is exposed here.
          </p>
          <PricingEditor value={pricing} onChange={setPricing} />
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setPricing(EMPTY_PRICING);
                patch('pricing', null);
              }}
            >
              Clear pricing
            </Button>
          </div>
          <details className="rounded border border-border/60 bg-bg-subtle/40 p-3">
            <summary className="cursor-pointer text-[11px] font-semibold uppercase tracking-wider text-fg-subtle">
              Advanced — view JSON
            </summary>
            <pre className="mt-2 max-h-64 overflow-auto rounded bg-bg p-3 font-mono text-[11px] text-fg-muted">
{JSON.stringify(pricing, null, 2)}
            </pre>
          </details>
        </div>
      </details>

      {v.status_publish === 'published' && v.slug && (
        <a
          href={`https://vonlof.co/services#${v.slug}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-gold-500 hover:underline"
        >
          <ExternalLink className="h-3 w-3" />
          Preview on public site
        </a>
      )}

      <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" form="service-form" loading={submitting}>
          {v.id ? 'Save changes' : 'Create service'}
        </Button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------ helpers */

/**
 * Coerce an unknown DB value into the editor's typed shape. Existing rows may
 * have been written with the JSON-raw path or by hand before this editor
 * existed, so we accept partial/missing fields and fall back to safe defaults
 * instead of throwing.
 */
function normalisePricing(input: unknown): PricingValue {
  const o = (input ?? {}) as Partial<PricingValue>;
  return {
    headline: typeof o.headline === 'string' ? o.headline : undefined,
    lede: typeof o.lede === 'string' ? o.lede : undefined,
    categories: Array.isArray(o.categories)
      ? o.categories.filter((c): c is string => typeof c === 'string')
      : [],
    tiers: Array.isArray(o.tiers)
      ? o.tiers.map((t) => ({
          name: typeof t?.name === 'string' ? t.name : '',
          bestFor: typeof t?.bestFor === 'string' ? t.bestFor : '',
          length: typeof t?.length === 'string' ? t.length : '',
          includes: Array.isArray(t?.includes)
            ? t.includes.filter((i): i is string => typeof i === 'string')
            : [],
          priceGHS: typeof t?.priceGHS === 'number' ? t.priceGHS : 0,
          priceUSD: typeof t?.priceUSD === 'number' ? t.priceUSD : 0,
          popular: !!t?.popular,
        }))
      : [],
    monthly: Array.isArray(o.monthly)
      ? o.monthly.map((p) => ({
          name: typeof p?.name === 'string' ? p.name : '',
          videosPerMonth: typeof p?.videosPerMonth === 'string' ? p.videosPerMonth : '',
          bestFor: typeof p?.bestFor === 'string' ? p.bestFor : '',
          priceGHS: typeof p?.priceGHS === 'number' ? p.priceGHS : 0,
          priceUSD: typeof p?.priceUSD === 'number' ? p.priceUSD : 0,
          popular: !!p?.popular,
        }))
      : [],
    addons: Array.isArray(o.addons)
      ? o.addons.map((a) => ({
          name: typeof a?.name === 'string' ? a.name : '',
          price: typeof a?.price === 'string' ? a.price : '',
        }))
      : [],
    rules: {
      footage: typeof o.rules?.footage === 'string' ? o.rules.footage : undefined,
      revisions: typeof o.rules?.revisions === 'string' ? o.rules.revisions : undefined,
      payment: typeof o.rules?.payment === 'string' ? o.rules.payment : undefined,
      delivery: typeof o.rules?.delivery === 'string' ? o.rules.delivery : undefined,
    },
    bundle: o.bundle && typeof o.bundle === 'object'
      ? {
          headline: typeof o.bundle.headline === 'string' ? o.bundle.headline : '',
          body: typeof o.bundle.body === 'string' ? o.bundle.body : '',
          ctaLabel: typeof o.bundle.ctaLabel === 'string' ? o.bundle.ctaLabel : undefined,
          ctaHref: typeof o.bundle.ctaHref === 'string' ? o.bundle.ctaHref : undefined,
        }
      : undefined,
  };
}

/** Empty across every meaningful dimension — used to clear the column. */
function isPricingEmpty(p: PricingValue): boolean {
  const noIntro = !p.headline && !p.lede && (p.categories?.length ?? 0) === 0;
  const noArrays = p.tiers.length === 0 && p.monthly.length === 0 && p.addons.length === 0;
  const noRules = !p.rules.footage && !p.rules.revisions && !p.rules.payment && !p.rules.delivery;
  const noBundle = !p.bundle
    || (!p.bundle.headline && !p.bundle.body && !p.bundle.ctaLabel && !p.bundle.ctaHref);
  return noIntro && noArrays && noRules && noBundle;
}
